// @givmo/board — after "Add a member", the same dialog asks whether to invite the new member
// (CBG-UX9, D-CBG-38; design stop design-282's frames s1–s4, c1 and p1).
//
// A new officer added a seat and never learned it could be invited: the invitation lived only in the
// row's ⋯ menu. So once the add has saved, the dialog's next view asks the question, with the seat's
// email of record filled in, "Not now" and "Invite" (Invite the primary button, Jonathan's mark), and
// a line saying where the invitation is later. It is a VIEW of the dialog the add was made in — the
// host keeps its `Dialog.Root`, so nothing flashes and the add already stands when it is asked —
// and Not now, Escape and the scrim each leave the member added and not invited, as the add always
// has.
//
// THE ADDRESS IS THE SEAT'S (design §9 question 1). An invitation only works at the seat's email of
// record: they join with a Givmo account at that address, or with a code sent there (D-CBG-17
// decision 1). So editing the field and pressing Invite is two acts, in this order: the existing
// update (`PATCH …/members/{id}`, the address alone), then the existing mint (`POST …/members/{id}/
// access-links`). The order matters: an address change retires the seat's unconsumed invitations, so
// a mint before it would be dead on arrival. A refused update mints nothing; an update that lands
// before a refused mint leaves the new address in place, and Invite then tries the mint alone. The
// address compares as the backend compares it (trimmed, lowercased), so a change of case is no change.
//
// EITHER ANSWER KEEPS AN EDITED ADDRESS (D-CBG-38: editing the email there changes the seat's email
// of record). A secretary who fixes a typo and presses Not now meant the fix: Not now makes the same
// update first, with the same refusals, and closes once it lands, inviting no one. An empty field is
// no address, so nothing is saved for it. Escape and the scrim are the dialog's cancel and save
// nothing.
//
// WHO IS ASKED is the host's to decide (design §7): only a person holding the invitation's own string
// (`board_member:grant_access`), for a new seat Board can invite — never on an edit, a former seat, an
// add made inside an election's result, or someone already seated added to a committee.
//
// After Invite the dialog is the shipped invitation, word for word (`InvitationOutcome`,
// `InvitationActions`): the email went, or dev's recipient guard held it.

import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError, type BoardAccessGrantOut } from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, TextField } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';
import { useDirectorAccessLink } from '../../director/director-access-link';
import {
  INVITATION_FAILED,
  InvitationActions,
  InvitationOutcome,
  invitationTitle,
  type InvitedSeat,
} from './invitation-dialog';
import { inviteStepWords as w } from './invite-step-words';

/** An address as the backend keeps it: trimmed, lowercased (`normalize_email_of_record`). */
export function asKept(address: string): string {
  return address.trim().toLowerCase();
}

export interface InviteStepState {
  /** The seat on offer, as it now stands (its address follows the step's update); null: no step. */
  seat: InvitedSeat | null;
  /** A seat for someone who isn't on the board: the committees it serves on, which its words name. */
  committees: readonly string[] | null;
  /** Ask about a seat just added. */
  offer: (seat: InvitedSeat, committees?: readonly string[] | null) => void;
  /** The dialog's title: the question, then the invitation's own once it is made. */
  title: string;
  /** The update or the mint is on its way: nothing closes the dialog. */
  busy: boolean;
  /** Not now's update is on its way (its button shows the work, Invite's doesn't). */
  savingForLater: boolean;
  email: string;
  setEmail: (email: string) => void;
  /** The field holds an address that differs from the seat's: Invite or Not now changes it first. */
  addressChanged: boolean;
  invite: () => void;
  /** Not now: an edited address is saved first, then `close`; otherwise `close` at once. */
  notNow: (close: () => void) => void;
  error: string | null;
  grant: BoardAccessGrantOut | null;
}

/**
 * The step's state and its two acts. `onChanged` runs after each write lands — the address, then the
 * invitation — so the page behind always says where the seat is.
 */
export function useInviteStep(orgRef: string, onChanged: () => void): InviteStepState {
  const operations = useBoardOperations();
  const [seat, setSeat] = useState<InvitedSeat | null>(null);
  const [committees, setCommittees] = useState<readonly string[] | null>(null);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [grant, setGrant] = useState<BoardAccessGrantOut | null>(null);

  const mint = useMutation({
    mutationFn: async (invitee: InvitedSeat) => {
      const { data } = await operations.officerMintBoardAccessLink({
        path: { org_ref: orgRef, member_id: invitee.id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (data) => {
      setGrant(data);
      onChanged();
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : INVITATION_FAILED),
  });

  const changeAddress = useMutation({
    mutationFn: async ({
      invitee,
      address,
    }: {
      invitee: InvitedSeat;
      address: string;
      /** What follows a landed update: Invite's mint, or Not now's close (its own callback). */
      then: 'mint' | 'close';
    }) => {
      const { data } = await operations.officerUpdateBoardMember({
        path: { org_ref: orgRef, member_id: invitee.id },
        body: { email_of_record: address },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (data, { invitee, address, then }) => {
      const moved = {
        ...invitee,
        email_of_record:
          typeof data?.email_of_record === 'string' ? data.email_of_record : asKept(address),
      };
      setSeat(moved);
      onChanged();
      if (then === 'mint') mint.mutate(moved);
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : w.saveFailed),
  });

  const addressChanged =
    seat !== null && email.trim().length > 0 && asKept(email) !== asKept(seat.email_of_record);
  const heading = seat === null ? '' : invitationTitle(seat.display_name, committees);

  return {
    seat,
    committees,
    offer: (next, nextCommittees = null) => {
      setSeat(next);
      setCommittees(nextCommittees);
      setEmail(next.email_of_record);
      setError(null);
      setGrant(null);
    },
    title: seat === null || grant !== null ? heading : w.title(heading),
    busy: changeAddress.isPending || mint.isPending,
    savingForLater: changeAddress.isPending && changeAddress.variables?.then === 'close',
    email,
    setEmail,
    addressChanged,
    invite: () => {
      if (seat === null) return;
      setError(null);
      if (addressChanged) {
        changeAddress.mutate({ invitee: seat, address: email.trim(), then: 'mint' });
      } else mint.mutate(seat);
    },
    notNow: (close) => {
      if (seat === null || !addressChanged) {
        close();
        return;
      }
      setError(null);
      changeAddress.mutate(
        { invitee: seat, address: email.trim(), then: 'close' },
        { onSuccess: close },
      );
    },
    error,
    grant,
  };
}

/**
 * The step's view, inside the host's dialog: the question, then the invitation once it is made. Focus
 * opens on the title each time, so a screen reader hears it and Enter presses neither button.
 */
export function InviteStep({
  step,
  testIdPrefix,
  onClose,
}: {
  step: InviteStepState;
  /** The host dialog's own prefix (`roster-seat`, `committee-member`): `<prefix>-invite-…`. */
  testIdPrefix: string;
  onClose: () => void;
}) {
  const linkFor = useDirectorAccessLink();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const invited = step.grant !== null;
  useEffect(() => {
    titleRef.current?.focus();
  }, [invited]);

  const seat = step.seat;
  if (seat === null) return null;
  const title = (
    <Dialog.Title
      ref={titleRef}
      tabIndex={-1}
      className="font-sans text-s18 font-w700 tracking-givmo text-text-strong outline-none"
    >
      {step.title}
    </Dialog.Title>
  );

  if (step.grant !== null) {
    const link = linkFor(step.grant.access_token);
    return (
      <>
        {title}
        <InvitationOutcome seat={seat} grant={step.grant} link={link} />
        <InvitationActions link={link} onClose={onClose} />
      </>
    );
  }

  return (
    <>
      {title}
      <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {w.lead}
      </p>
      <TextField
        label={w.emailLabel}
        type="email"
        value={step.email}
        onChange={(event) => step.setEmail(event.target.value)}
        testId={`${testIdPrefix}-invite-email`}
      />
      {step.addressChanged ? (
        <FieldNote testId={`${testIdPrefix}-invite-address-change`}>
          {w.addressChanges(seat.display_name)}
        </FieldNote>
      ) : null}
      <p
        className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
        data-testid={`${testIdPrefix}-invite-later`}
      >
        {step.committees !== null ? w.laterFromTheirPage : w.later}
      </p>
      {step.error !== null ? (
        <ErrorMessage testId={`${testIdPrefix}-invite-error`}>{step.error}</ErrorMessage>
      ) : null}
      <div className="flex justify-end gap-10">
        <Button
          appearance="saas"
          tone="secondary"
          loading={step.savingForLater}
          disabled={step.busy}
          onClick={() => step.notNow(onClose)}
          testId={`${testIdPrefix}-invite-not-now`}
        >
          {w.notNow}
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          loading={step.busy && !step.savingForLater}
          disabled={step.busy || step.email.trim().length === 0}
          onClick={step.invite}
          testId={`${testIdPrefix}-invite-confirm`}
        >
          {w.invite}
        </Button>
      </div>
    </>
  );
}
